import type { ReactNode } from 'react';

interface EmptyStateProps {
  /** The main heading text */
  title: string;
  /** Optional subtitle or description text */
  description?: string;
  /** Custom className for the container */
  className?: string;
  /** Child elements to render below the description */
  children?: ReactNode;
  /** Optional illustration to display */
  illustration?: ReactNode;
}

/**
 * A reusable empty state component for displaying when there's no content to show.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No messages yet"
 *   description="Start a conversation to see messages here" />}
 * >
 *   <Button>Start Chat</Button>
 * </EmptyState>
 * ```
 */
export function EmptyState({ title, description, className = '', children, illustration }: EmptyStateProps) {
  return (
    <div className={`flex-1 flex flex-col items-center justify-center gap-3 ${className}`}>
      {illustration && <div className="mb-4">{illustration}</div>}

      <h3 className="text-2xl font-display font-light text-center">{title}</h3>
      {description && <p className="text-muted-foreground text-center max-w-md">{description}</p>}
      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}
